<template>
  <section class="management-section" data-testid="management-insights">
    <ManagementSectionHeader :title="$t('UserProjects.management.insights.title')" />

    <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
      <article
        v-for="card in cards"
        :key="card.id"
        class="management-panel-card p-4"
        :data-testid="`management-insight-${card.id}`"
      >
        <div class="flex items-center gap-2 mb-2">
          <i :class="[card.icon, 'text-base']" :style="{ color: card.color }" />
          <p class="text-xs font-medium text-[var(--color-dark)] m-0 flex-1">{{ card.title }}</p>
          <span class="text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-[var(--color-light-grey)] text-[var(--color-disabled)] uppercase">
            {{ card.badge }}
          </span>
        </div>

        <p class="text-[22px] font-bold text-[var(--color-dark)] leading-tight m-0">{{ card.value }}</p>
        <p class="text-[11px] text-[var(--color-disabled)] mt-0.5 mb-2 m-0">{{ card.subtitle }}</p>
        <p class="text-xs text-[var(--color-dark)] leading-relaxed pt-2 m-0 border-t border-[var(--color-light-grey)]">
          {{ card.detail }}
        </p>
      </article>
    </div>
  </section>
</template>

<script>
import ManagementSectionHeader from '@/components/CustomerApp/UserProjects/ManagementSectionHeader.vue';

export default {
  name: 'ManagementInsightsStrip',
  components: { ManagementSectionHeader },
  props: {
    insights: {
      type: Object,
      default: null,
    },
  },
  computed: {
    cards() {
      return [
        {
          id: 'measures',
          icon: 'pi pi-chart-line',
          color: 'var(--color-warning-text)',
          badge: this.$t('UserProjects.management.insights.trendTag'),
          title: this.$t('UserProjects.management.insights.measuresEffectiveness'),
          value: this.measuresValue !== null ? `${this.measuresValue}%` : '—',
          subtitle: this.$t('UserProjects.management.insights.measuresEffectivenessHint'),
          detail: this.measuresDetail,
        },
        {
          id: 'decision-speed',
          icon: 'pi pi-gauge',
          color: 'var(--color-error-text)',
          badge: this.$t('UserProjects.management.insights.currentTag'),
          title: this.$t('UserProjects.management.insights.decisionSpeed'),
          value: this.decisionSpeed !== null
            ? `${this.decisionSpeed} ${this.$t('UserProjects.management.insights.daysSuffix')}`
            : '—',
          subtitle: this.$t('UserProjects.management.insights.decisionSpeedHint'),
          detail: this.decisionSpeedDetail,
        },
        {
          id: 'patterns',
          icon: 'pi pi-replay',
          color: 'var(--color-orange)',
          badge: this.$t('UserProjects.management.insights.patternTag'),
          title: this.$t('UserProjects.management.insights.recurringPatterns'),
          value: `${this.patternCount} ${this.$t('UserProjects.management.insights.patternsSuffix')}`,
          subtitle: this.$t('UserProjects.management.insights.recurringPatternsHint'),
          detail: this.recurringPatternsDetail,
        },
      ];
    },
    measuresValue() {
      if (!this.insights || this.insights.measuresEffectivenessInsufficientData) return null;
      return this.insights.measuresEffectivenessPercent;
    },
    decisionSpeed() {
      return this.insights?.decisionSpeedDaysAvg ?? null;
    },
    patternCount() {
      return this.insights?.recurringCausePatternCount ?? 0;
    },
    measuresDetail() {
      if (this.measuresValue !== null) {
        const noEffect = this.insights?.measuresNoEffectPercent
          ?? Math.max(0, 100 - this.measuresValue);
        const repeatRate = this.insights?.measuresRepeatRatePercent ?? noEffect;
        return this.$t('UserProjects.management.insights.measuresEffectivenessDetail', {
          percent: this.measuresValue,
          noEffect,
          repeatRate,
        });
      }
      return this.$t('UserProjects.management.insights.measuresEffectivenessDetailInsufficient');
    },
    decisionSpeedDetail() {
      const overdue = this.insights?.decisionSpeedOverdueCount ?? 0;
      const escalated = this.insights?.decisionSpeedEscalatedCount ?? 0;
      const dist = this.insights?.escalationDistribution;
      const distLabel = dist
        ? this.$t('UserProjects.management.insights.escalationDistributionShort', {
          l0: dist.level0 ?? 0,
          l1: dist.level1 ?? 0,
          l2: dist.level2 ?? 0,
          l3: dist.level3 ?? 0,
        })
        : '';
      if (overdue === 0 && escalated === 0) {
        return distLabel
          || this.$t('UserProjects.management.insights.decisionSpeedDetailNone');
      }
      const base = this.$t('UserProjects.management.insights.decisionSpeedDetail', { overdue, escalated });
      return distLabel ? `${base} · ${distLabel}` : base;
    },
    recurringPatternsDetail() {
      const labels = this.insights?.topRecurringCauseLabels ?? [];
      if (labels.length === 0) {
        return this.$t('UserProjects.management.insights.recurringPatternsDetailNone');
      }
      return labels.join(' · ');
    },
  },
};
</script>

<style scoped>
@import './management-section.css';
</style>
